import React, { lazy, Suspense, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import Landing from './pages/Landing';
import Navbar from './components/Navbar';
import VerificationBanner from './components/VerificationBanner';
import CookieBanner from './components/CookieBanner';
import Seo from './components/Seo';
import { ConfirmDialogHost } from './components/ConfirmDialog';
import './App.css';

// Keep authenticated, legal and charting screens out of the landing-page
// entry bundle. Chart.js is pulled only when a history surface is opened.
const Login = lazy(() => import('./pages/Login'));
const Register = lazy(() => import('./pages/Register'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const NotificationHistory = lazy(() => import('./pages/NotificationHistory'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const SetPassword = lazy(() => import('./pages/SetPassword'));
const Profile = lazy(() => import('./pages/Profile'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const VerifyEmail = lazy(() => import('./pages/VerifyEmail'));
const Terms = lazy(() => import('./pages/Terms'));
const Privacy = lazy(() => import('./pages/Privacy'));
const Legal = lazy(() => import('./pages/Legal'));
const About = lazy(() => import('./pages/About'));
const Goodbye = lazy(() => import('./pages/Goodbye'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Feedback = lazy(() => import('./pages/Feedback'));
const EmailPreferences = lazy(() => import('./pages/EmailPreferences'));
const Unsubscribed = lazy(() => import('./pages/Unsubscribed'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));

const HOME_SCHEMA = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Organization',
      '@id': 'https://pricehuntify.com/#organization',
      name: 'Pricehuntify',
      url: 'https://pricehuntify.com/',
      logo: 'https://pricehuntify.com/app-icon-512.png',
      email: 'support@pricehuntify.com',
      address: {
        '@type': 'PostalAddress',
        addressLocality: 'Sydney',
        addressCountry: 'AU',
      },
    },
    {
      '@type': 'WebSite',
      '@id': 'https://pricehuntify.com/#website',
      url: 'https://pricehuntify.com/',
      name: 'Pricehuntify',
      publisher: { '@id': 'https://pricehuntify.com/#organization' },
      inLanguage: 'en',
    },
  ],
};

const ROUTE_SEO = {
  '/': {
    title: 'Free Amazon Price Tracker & Price Drop Alerts | Pricehuntify',
    description: 'Track Amazon prices across nine regions. Set your target and receive an email when the price drops. Free to use—no app or credit card required.',
    schema: HOME_SCHEMA,
  },
  '/about': {
    title: 'About Pricehuntify | Independent Amazon Price Tracker',
    description: 'Learn why Pricehuntify was built in Sydney and how its independent Amazon price alerts help shoppers wait for the right price.',
  },
  '/terms': {
    title: 'Terms and Conditions | Pricehuntify',
    description: 'The terms that govern use of the Pricehuntify price-tracking and alert service.',
  },
  '/privacy': {
    title: 'Privacy Policy | Pricehuntify',
    description: 'How Pricehuntify collects, uses, protects and deletes account and product-tracking data.',
  },
  '/legal': {
    title: 'Legal, Privacy and Affiliate Disclosure | Pricehuntify',
    description: 'A plain-language summary of Pricehuntify privacy, data, outbound-link and service policies.',
  },
};

function RouteMetadata() {
  const { pathname } = useLocation();
  if (/^\/product\/[A-Za-z0-9]{10}$/.test(pathname)) return null;

  // `schema` is passed explicitly (null when the route has none) so a
  // client-side navigation away from a product page clears that page's
  // structured data. Omitting the prop means "leave the server's tag alone",
  // which is only correct for /product/:asin (handled above).
  const page = ROUTE_SEO[pathname];
  if (page) return <Seo {...page} schema={page.schema ?? null} canonical={pathname} />;

  const label = pathname === '/register' ? 'Create Account' : pathname === '/login' ? 'Sign In' : 'Account';
  return (
    <Seo
      title={`${label} | Pricehuntify`}
      description="Pricehuntify account and service page."
      canonical={pathname}
      schema={null}
      noindex
    />
  );
}

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
};

// Public route (redirect to dashboard if logged in)
const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  return !user ? children : <Navigate to="/dashboard" />;
};

// Admin-only route
const AdminRoute = ({ children }) => {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" />;
  if (!user.isAdmin) return <Navigate to="/dashboard" />;
  return children;
};

function AppFooter() {
  return (
    <footer className="dt-footer">
      <span>© 2026 PRICEHUNTIFY · INDEPENDENT · NOT SPONSORED OR ENDORSED BY AMAZON</span>
      <span>
        <Link to="/legal">TERMS · PRIVACY</Link>
        {' · '}
        <Link to="/feedback">CONTACT</Link>
      </span>
    </footer>
  );
}

function AppContent() {
  const { user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'page_view', { page_path: location.pathname + location.search });
    }
  }, [location]);

  return (
    <div className="App">
      <RouteMetadata />
      {user && <Navbar />}
      {user && <VerificationBanner />}
      <div className="App-routes">
      <Suspense fallback={<div className="pd-state">Loading Pricehuntify…</div>}>
      <Routes>
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <Landing />} />
        <Route
          path="/login"
          element={
            <PublicRoute>
              <Login />
            </PublicRoute>
          }
        />
        <Route
          path="/register"
          element={
            <PublicRoute>
              <Register />
            </PublicRoute>
          }
        />
        <Route
          path="/forgot-password"
          element={
            <PublicRoute>
              <ForgotPassword />
            </PublicRoute>
          }
        />
        <Route path="/reset-password" element={<ResetPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        <Route path="/verify-email" element={<VerifyEmail />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/legal" element={<Legal />} />
        <Route path="/about" element={<About />} />
        <Route
          path="/feedback"
          element={
            <ProtectedRoute>
              <Feedback />
            </ProtectedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <Profile />
            </ProtectedRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <Dashboard />
            </ProtectedRoute>
          }
        />
        <Route
          path="/notifications"
          element={
            <ProtectedRoute>
              <NotificationHistory />
            </ProtectedRoute>
          }
        />
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminDashboard />
            </AdminRoute>
          }
        />
        <Route
          path="/preferences"
          element={
            <ProtectedRoute>
              <EmailPreferences />
            </ProtectedRoute>
          }
        />
        <Route path="/unsubscribed" element={<Unsubscribed />} />
        <Route path="/goodbye" element={<Goodbye />} />
        <Route path="/product/:asin" element={<ProductDetail />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      </div>
      {user && <AppFooter />}
      <CookieBanner />
      <ConfirmDialogHost />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

export default App;
